import { TypedDocumentNode, gql } from '@apollo/client';
import {
  faCalendarDay,
  faCheck,
  faShirt,
} from '@fortawesome/pro-regular-svg-icons';
import classNames from 'classnames';
import { addHours, isPast } from 'date-fns';
import { ComponentType, PropsWithChildren, ReactNode } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import styled, { CSSProperties, css } from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Box } from '@sorare/core/src/atoms/icons/Box';
import { Whistle } from '@sorare/core/src/atoms/icons/Whistle';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import {
  BodyM,
  BodyS,
  HeadlineL,
  LabelL,
} from '@sorare/core/src/atoms/typography';
import { groupBy, sortBy, uniqueBy } from '@sorare/core/src/lib/arrays';
import { glossary } from '@sorare/core/src/lib/glossary';

import FixtureTimelineGame from './FixtureTimelineGame';
import { MainItemContent } from './MainItemContent';
import {
  FixtureTimeline_so5Fixture,
  FixtureTimeline_so5Lineup,
} from './__generated__/index.graphql';

const Wrapper = styled(Vertical)`
  isolation: isolate;
  padding: var(--double-unit);
  --dot-width: calc(var(--quadruple-unit));
`;

const TimelineRowWrapper = styled(Horizontal)`
  border-radius: var(--unit);
  align-items: flex-start;
  position: relative;
`;
const Dot = styled(Horizontal).attrs({ gap: 0, center: true })<{
  completed?: boolean;
}>`
  width: var(--dot-width);
  height: var(--dot-width);
  border-radius: 50%;
  box-sizing: border-box;
  z-index: 1;
  ${({ completed }) =>
    completed
      ? css`
          background: var(--c-score-high);
          color: var(--c-neutral-100);
        `
      : css`
          background: var(--c-neutral-150);
          color: var(--c-neutral-1000);
        `}
`;

const VerticalLine = styled.div`
  --line-width: 10px;
  width: var(--line-width);
  background: linear-gradient(
    to bottom,
    var(--c-score-high) var(--progress),
    var(--c-neutral-150) var(--progress),
    var(--c-neutral-150) 100%
  );
  min-height: var(--quadruple-unit);
  margin-bottom: calc(-1 * var(--intermediate-unit));
  position: absolute;
  top: calc(var(--dot-width) - 1px);
  left: calc(var(--dot-width) / 2 - var(--line-width) / 2);
  z-index: -1;
  bottom: 0;
`;

const AppearanceImg = styled.img`
  width: var(--quadruple-unit);
`;

const RowContent = styled(Vertical)`
  padding: var(--half-unit) 0 var(--quadruple-unit) 0;
  flex: 1;
  &.isLastRow {
    padding-bottom: 0;
  }
`;

const Cards = styled(Horizontal)`
  flex-wrap: wrap;
`;

const Date = styled(BodyS)`
  font-variant-numeric: tabular-nums;
`;

type TimelineRowProps = {
  label?: ReactNode;
  date?: Date;
  progress: number;
  isLastRow?: boolean;
  dot?: (
    Dot: ComponentType<PropsWithChildren<{ completed?: boolean }>>
  ) => ReactNode;
  icon?: ReactNode;
};

const TimelineRow = ({
  label,
  progress,
  children,
  date,
  isLastRow,
  dot,
  icon,
}: PropsWithChildren<TimelineRowProps>) => {
  return (
    <TimelineRowWrapper>
      {!isLastRow && (
        <VerticalLine
          style={
            {
              '--progress': `${progress * 100}%`,
            } as CSSProperties
          }
        />
      )}
      {dot?.(Dot) || <Dot completed={progress > 0}>{icon}</Dot>}
      <RowContent className={classNames({ isLastRow })}>
        <LabelL
          bold
          color={progress > 0 ? 'var(--c-score-high)' : 'currentColor'}
        >
          {label || (
            <FormattedDate
              value={date}
              day="numeric"
              month="short"
              weekday="long"
            />
          )}
        </LabelL>
        <div>{children}</div>
      </RowContent>
    </TimelineRowWrapper>
  );
};

type Props = {
  so5Fixture: FixtureTimeline_so5Fixture;
  so5Lineup?: FixtureTimeline_so5Lineup;
};

export const FixtureTimeline = ({ so5Fixture, so5Lineup }: Props) => {
  const { formatDate } = useIntl();
  const games = sortBy(
    game => game?.date || '',
    uniqueBy(
      game => game?.id || '',
      so5Lineup?.so5Appearances
        .flatMap(appearance => appearance.eligiblePlayerGameScores)
        .map(gameScore => gameScore.anyGame) || []
    )
  ).filter(Boolean);

  const gamesByDate = groupBy(games, game =>
    formatDate(game.date, { format: 'yyyy-MM-dd' })
  );
  const lineupSubmitted = !!so5Lineup && !so5Lineup.draft;

  return (
    <Wrapper gap={3}>
      <HeadlineL className="text-center">
        <FormattedMessage
          id="FixtureTimeline.title"
          defaultMessage="Your Schedule"
        />
      </HeadlineL>
      <Vertical gap={0}>
        <TimelineRow
          label={
            <FormattedMessage
              id="FixtureTimeline.setYourLineup"
              defaultMessage="Set your lineup"
            />
          }
          progress={lineupSubmitted ? 1 : 0}
          dot={D => (
            <D completed={lineupSubmitted}>
              {lineupSubmitted ? (
                <FontAwesomeIcon icon={faCheck} />
              ) : (
                <FontAwesomeIcon icon={faShirt} />
              )}
            </D>
          )}
        />
        {!lineupSubmitted && (
          // display gameweek starts if lineup not set to avoid an empty timeline
          <TimelineRow
            date={so5Fixture.startDate}
            icon={<Whistle />}
            progress={isPast(so5Fixture.startDate) ? 1 : 0}
          >
            <Horizontal>
              <Date color="var(--c-neutral-700)">
                <FormattedDate
                  value={so5Fixture.startDate}
                  hour="2-digit"
                  minute="2-digit"
                />
              </Date>
              <MainItemContent>
                <BodyM>
                  <FormattedMessage
                    id="FixtureStatus.gameweekStarts"
                    defaultMessage="Gameweek starts"
                  />
                </BodyM>
              </MainItemContent>
            </Horizontal>
          </TimelineRow>
        )}
        {Object.entries(gamesByDate).map(([date, gamesList]) => (
          <TimelineRow
            key={date}
            date={gamesList?.at(0)?.date}
            icon={<FontAwesomeIcon icon={faCalendarDay} />}
            progress={
              (gamesList?.reduce(
                (acc, cur) => (isPast(cur.date) ? acc + 1 : acc),
                0
              ) || 0) / (gamesList?.length || 1)
            }
          >
            {gamesList?.map(game => {
              const correspondingAppearances = so5Lineup?.so5Appearances.filter(
                appearance =>
                  appearance.eligiblePlayerGameScores.some(
                    gameScore => gameScore.anyGame?.id === game.id
                  )
              );
              return (
                <SBHorizontal key={game.id} gap={8}>
                  <FixtureTimelineGame game={game} />
                  <Cards>
                    {correspondingAppearances?.map(appearance => (
                      <AppearanceImg
                        src={appearance.tinifiedPictureUrl ?? undefined}
                        alt=""
                        key={appearance.id}
                      />
                    ))}
                  </Cards>
                </SBHorizontal>
              );
            })}
          </TimelineRow>
        ))}
        <TimelineRow
          date={addHours(so5Fixture.endDate, 4)}
          isLastRow
          icon={<Box />}
          progress={isPast(so5Fixture.endDate) ? 1 : 0}
        >
          <Horizontal>
            <Date color="var(--c-neutral-700)">
              <FormattedDate
                value={addHours(so5Fixture.endDate, 4)}
                hour="2-digit"
                minute="2-digit"
              />
            </Date>
            <MainItemContent>
              <BodyM>
                <FormattedMessage {...glossary.rewards} />
              </BodyM>
            </MainItemContent>
          </Horizontal>
        </TimelineRow>
      </Vertical>
    </Wrapper>
  );
};

FixtureTimeline.fragments = {
  so5Fixture: gql`
    fragment FixtureTimeline_so5Fixture on So5Fixture {
      slug
      startDate
      endDate
    }
  ` as TypedDocumentNode<FixtureTimeline_so5Fixture>,
  so5Lineup: gql`
    fragment FixtureTimeline_so5Lineup on So5Lineup {
      id
      draft
      so5Appearances {
        id
        tinifiedPictureUrl: pictureUrl(derivative: "tinified")
        eligiblePlayerGameScores {
          id
          anyGame {
            id
            date
            homeTeam {
              slug
              code
            }
            awayTeam {
              slug
              code
            }
            ...FixtureTimelineGame_game
          }
        }
      }
    }
    ${FixtureTimelineGame.fragments.game}
  ` as TypedDocumentNode<FixtureTimeline_so5Lineup>,
};
